import React from 'react';

import { lowerCase } from 'lodash';

import { faBookOpen } from '@fortawesome/pro-regular-svg-icons/faBookOpen';
import { faCalendarPlus } from '@fortawesome/pro-regular-svg-icons/faCalendarPlus';
// eslint-disable-next-line no-unused-vars
import { faChartLineDown } from '@fortawesome/pro-regular-svg-icons/faChartLineDown';
// eslint-disable-next-line no-unused-vars
import { faChartLineUp } from '@fortawesome/pro-regular-svg-icons/faChartLineUp';
import { faChartSimple } from '@fortawesome/pro-regular-svg-icons/faChartSimple';
import { faFaceFrown } from '@fortawesome/pro-regular-svg-icons/faFaceFrown';
import { faFaceSmile } from '@fortawesome/pro-regular-svg-icons/faFaceSmile';
import { faGear } from '@fortawesome/pro-regular-svg-icons/faGear';
import { faLightbulbOn } from '@fortawesome/pro-regular-svg-icons/faLightbulbOn';
import { faMessageLines } from '@fortawesome/pro-regular-svg-icons/faMessageLines';
import { faPartyHorn } from '@fortawesome/pro-regular-svg-icons/faPartyHorn';
import { faSirenOn } from '@fortawesome/pro-regular-svg-icons/faSirenOn';

import FontAwesomeIcon from 'components/Common/FontAwesomeIcon';

import {
  OVERALL_CATEGORY,
  CHECKINS_ANSWER_CATEGORIES_WITH_OVERALL,
} from 'constants/checkins';

import USER_CAPABILITIES from 'constants/userCapabilities';

import {
  FILTER_GRADES,
  FILTER_SCHOOLS,
  FILTER_CASELOADS,
  FILTER_STUDENT_TAGS,
  FILTER_STUDENT_TAGS_OFFICIAL,
} from 'modules/StudentScopeFilter/constants';

export const MODULE_NAME = 'checkins';

export const SUGGESTED_LESSONS_TAB_ID = 'suggested-lessons';

export const TABS = [
  {
    id: 'overview',
    icon: <FontAwesomeIcon icon={faChartSimple} />,
    label: 'Overview',
  },
  {
    id: 'schedule',
    icon: <FontAwesomeIcon icon={faCalendarPlus} />,
    label: 'Schedule',
  },
  {
    id: SUGGESTED_LESSONS_TAB_ID,
    icon: <FontAwesomeIcon icon={faBookOpen} />,
    label: 'Suggested Lessons',
  },
  {
    id: 'insights',
    icon: <FontAwesomeIcon icon={faLightbulbOn} />,
    label: 'Insights',
  },
  {
    id: 'settings',
    icon: <FontAwesomeIcon icon={faGear} />,
    label: 'Settings',
    capability: USER_CAPABILITIES.CAPABILITY_SEL_CHECKINS_SCHEDULE,
  },
];

export const STUDENT_SCOPE_FILTERS = [
  FILTER_GRADES,
  FILTER_SCHOOLS,
  FILTER_CASELOADS,
  FILTER_STUDENT_TAGS,
  FILTER_STUDENT_TAGS_OFFICIAL,
];

export const STUDENT_SCOPE_INSTANCE = 'elCheckinOverview';

/**
 * Overview
 */

/**
 * Dependant in CHECKINS_ANSWER_CATEGORIES in checkins/constants
 */
export const LATEST_ANSWERS_AXIS_LEFT_EMOJI_COORDINATES = {
  Emotional: { translateX: 102, translateY: 15 },
  Physical: { translateX: 90, translateY: 15 },
  Social: { translateX: 78, translateY: 15 },
  Cognitive: { translateX: 96, translateY: 15 },
};

export const WEEK = 'week';
export const TWO_WEEKS = 'two weeks';
export const MONTH = 'month';

const PAST_7_DAYS = 'Past 7 days';
const PAST_14_DAYS = 'Past 14 days';
const PAST_30_DAYS = 'Past 30 days';

export const TRENDS_DROPDOWN_OPTIONS = [
  { name: PAST_7_DAYS, id: WEEK },
  { name: PAST_14_DAYS, id: TWO_WEEKS },
  { name: PAST_30_DAYS, id: MONTH },
];

export const INSIGHTS_SORTING_OPTIONS = [
  { name: 'Most Matches', id: 'desc,user_survey_count' },
  { name: 'Least Matches', id: 'asc,user_survey_count' },
  { name: 'Newest - Oldest', id: 'desc,last_survey_completed_datetime' },
  { name: 'Oldest - Newest', id: 'asc,last_survey_completed_datetime' },
];

export const DAYS_RANGE = {
  [WEEK]: 6,
  [TWO_WEEKS]: 13,
  [MONTH]: 29,
};

/**
 * Schedule
 */

export const GRADE_NAME_MAP = Object.freeze({
  0: 'Kindergarten',
  1: '1st grade',
  2: '2nd grade',
  3: '3rd grade',
  4: '4th grade',
  5: '5th grade',
  6: '6th grade',
  7: '7th grade',
  8: '8th grade',
  9: '9th grade',
  10: '10th grade',
  11: '11th grade',
  12: '12th grade',
});

/**
 * Insights
 */

export const INSIGHTS_LOADING_SKELETON_COUNT = 4;
export const INSIGHTS_LOADING_SKELETON_CHIPS_COUNT = 4;
export const DECREASED_ACTION = 'decreased';
export const REPORTED_ACTION = 'reported';
export const INCREASED_ACTION = 'increased';
export const WROTE_ACTION = 'wrote';
export const LEFT_NOTE_ACTION = 'left_note';
export const INSIGHTS_CARD_SIZE = 96;
export const INSIGHTS_PER_PAGE = 10;
export const INSIGHTS_DEFAULT_ORDER = 'desc';
export const INSIGHTS_DEFAULT_ORDER_BY = 'user_survey_count';
export const NEGATIVE_QUESTION_CHOICE_WEIGHTING = -1;
export const POSITIVE_QUESTION_CHOICE_WEIGHTING = 1;
export const NEGATIVE_ACTION_NAME = 'Chose a negative answer';
export const CELEBRATION_WORD_ACTION_NAME = 'Wrote a celebration word';

/**
 * Advanced Filter Options
 */

export const ACTIONS = [
  {
    id: REPORTED_ACTION,
    name: NEGATIVE_ACTION_NAME,
    icon: <FontAwesomeIcon icon={faFaceFrown} color="red" />,
  },
  {
    id: REPORTED_ACTION,
    name: 'Chose a positive answer',
    icon: <FontAwesomeIcon icon={faFaceSmile} color="green" />,
  },
  {
    id: LEFT_NOTE_ACTION,
    name: 'Left a note',
    icon: <FontAwesomeIcon icon={faMessageLines} color="blue" />,
  },
  {
    id: WROTE_ACTION,
    name: 'Wrote an alert word',
    icon: <FontAwesomeIcon icon={faSirenOn} color="red" />,
  },
  {
    id: WROTE_ACTION,
    name: CELEBRATION_WORD_ACTION_NAME,
    icon: <FontAwesomeIcon icon={faPartyHorn} color="green" />,
  },
  // {
  //   id: DECREASED_ACTION,
  //   name: 'A decreased score',
  //   icon: <FontAwesomeIcon icon={faChartLineDown} color="red" />,
  // },
  // {
  //   id: INCREASED_ACTION,
  //   name: 'An increased score',
  //   icon: <FontAwesomeIcon icon={faChartLineUp} color="green" />,
  // },
];

export const DURATION_WEEKS = [
  { id: 1, name: PAST_7_DAYS },
  { id: 2, name: PAST_14_DAYS },
  { id: 4, name: PAST_30_DAYS },
];

export const COUNT_OPTIONS = [
  { id: 1, name: 'At least 1 time' },
  { id: 2, name: 'At least 2 times' },
  { id: 3, name: 'At least 3 times' },
  { id: 4, name: 'At least 4 times' },
  { id: -1, name: 'On every check-in' },
];

export const CATEGORIES = CHECKINS_ANSWER_CATEGORIES_WITH_OVERALL.map(
  category => ({
    id: lowerCase(category),
    name: `${category} score`,
  }),
);

/**
 * Advanced Default Filters
 */

export const DEFAULT_DURATION_WEEKS = 4;
export const DEFAULT_NUMBER_OF_TIMES = 1;
export const DEFAULT_ACTION = REPORTED_ACTION;
export const DEFAULT_CATEGORY = lowerCase(OVERALL_CATEGORY);

export const ADVANCED_FILTERS_DEFAULT_VALUES = {
  action: ACTIONS.find(({ id }) => id === DEFAULT_ACTION),
  category: CATEGORIES.find(({ id }) => id === DEFAULT_CATEGORY),
  durationWeeks: DURATION_WEEKS.find(({ id }) => id === DEFAULT_DURATION_WEEKS),
  comparedDurationWeeks: DURATION_WEEKS.find(
    ({ id }) => id === DEFAULT_DURATION_WEEKS,
  ),
  count: COUNT_OPTIONS.find(({ id }) => id === DEFAULT_NUMBER_OF_TIMES),
};

export const CAPABILITY_DISABLED_TAB_MESSAGE =
  "To access this feature, your district's SchooLinks administrator must grant you the following capability: Schedule student check-ins";

export const ALL_GRADES_LABEL = 'All grades';
export const ALL_SCHOOLS_LABEL = 'All schools';
